import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Phone, ShieldCheck, Sparkles, MapPin, CheckCircle2, ChevronRight,
  Camera, Eye,
} from "lucide-react";
import { PROPERTIES, statusBadge } from "../data/properties";
import { trackPhoneClick } from "../lib/tracking";
import SaveHomeButton from "./SaveHomeButton";

function PropertyCard({ p }) {
  const sb = statusBadge(p);
  const [hover, setHover] = useState(false);

  return (
    <article
      className="group relative rounded-[22px] md:rounded-[26px] overflow-hidden bg-white border border-white/10 flex flex-col shadow-[0_20px_60px_-20px_rgba(0,0,0,0.55)] hover:shadow-[0_30px_80px_-20px_rgba(0,0,0,0.75)] transition-shadow"
      data-testid={`featured-card-${p.slug}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* Stretched link — entire card navigates except interactive controls (z-10) */}
      <Link
        to={p.href}
        aria-label={`View ${p.name}`}
        className="absolute inset-0 z-[1] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[color:var(--alpha-purple-300)] rounded-[22px] md:rounded-[26px]"
      >
        <span className="sr-only">View {p.name}</span>
      </Link>
      {/* Big cinematic photo */}
      <div className="relative aspect-[3/2] overflow-hidden bg-[color:var(--alpha-bg-soft)]">
        {/* Hero image (default) */}
        <img
          src={p.images[0].src}
          alt={p.images[0].alt}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover transition-all duration-[1200ms] ease-out"
          style={{ opacity: hover ? 0 : 1, transform: `scale(${hover ? 1.10 : 1})` }}
        />
        {/* Reveal-on-hover second image (cinematic crossfade) */}
        {p.images[1] && (
          <img
            src={p.images[1].src}
            alt=""
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover transition-all duration-[1200ms] ease-out"
            style={{ opacity: hover ? 1 : 0, transform: `scale(${hover ? 1.05 : 1})` }}
          />
        )}
        {/* Gradient overlays for legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0420]/80 via-[#0B0420]/15 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-transparent to-[#0B0420]/30" />
        {/* Hover glow */}
        <div
          className="absolute -inset-1 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
          style={{
            background:
              "radial-gradient(40% 50% at 30% 30%, rgba(134,89,199,0.55), transparent 70%), radial-gradient(40% 50% at 80% 70%, rgba(79,165,110,0.45), transparent 70%)",
            filter: "blur(40px)",
            mixBlendMode: "screen",
          }}
          aria-hidden
        />
        {/* Status badges */}
        <div className="absolute top-4 left-4 flex flex-col gap-1.5 items-start">
          <span className={`alpha-chip backdrop-blur !py-1 !px-2.5 !text-[10.5px] font-bold uppercase tracking-[0.2em] shadow-lg ${
            sb.tone === "green" ? "!bg-[color:var(--alpha-green-600)] !text-white !border-[color:var(--alpha-green-700)]"
            : sb.tone === "purple" ? "!bg-[color:var(--alpha-purple-700)] !text-white !border-[color:var(--alpha-purple-800)]"
            : "!bg-white !text-[color:var(--alpha-ink)] !border-white"
          }`}>
            {sb.label}
          </span>
          <span className="alpha-chip !bg-black/60 !text-white !border-white/20 backdrop-blur !py-1 !px-2.5 !text-[10.5px] font-semibold uppercase tracking-[0.18em]">
            {p.short.split(' · ')[0]}
          </span>
        </div>
        {/* Right-side specs */}
        <div className="absolute top-4 right-4 z-[2] flex flex-col gap-1.5 items-end">
          <SaveHomeButton slug={p.slug} homeName={p.name} variant="overlay" size="md" />
          <span className="alpha-chip !bg-white !text-[color:var(--alpha-purple-700)] !border-white !py-1 !px-2.5 !text-[11px] font-bold shadow-lg">
            1:1 & 1:2 only
          </span>
          <span className="alpha-chip !bg-black/55 !text-white !border-white/20 backdrop-blur !py-1 !px-2 !text-[10px] font-semibold inline-flex items-center gap-1">
            <Camera className="w-3 h-3" /> {p.images.length} photos
          </span>
        </div>
        {/* Bottom title */}
        <div className="absolute inset-x-0 bottom-0 p-5 text-white">
          <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.22em] text-white/85 font-semibold mb-1">
            <MapPin className="w-3 h-3" /> {p.suburb}, {p.state}
          </div>
          <h3 className="font-display font-semibold text-[22px] md:text-[26px] leading-[1.05] !text-white alpha-press">{p.name}</h3>
        </div>
        {/* "Take a tour" reveal */}
        <span className="absolute right-4 bottom-4 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500 bg-white text-[color:var(--alpha-purple-700)] text-[11px] font-bold uppercase tracking-[0.18em] rounded-full px-3 py-1.5 inline-flex items-center gap-1 shadow-2xl">
          <Eye className="w-3.5 h-3.5" /> Tour
        </span>
      </div>

      {/* Body */}
      <div className="p-5 md:p-6 flex-1 flex flex-col bg-white">
        <p className="text-sm text-[color:var(--alpha-mute)] line-clamp-2">{p.oneLiner}</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {p.features.slice(0, 3).map((f) => (
            <span key={f} className="alpha-chip alpha-chip-neutral !text-[11px] !py-1 !px-2.5">{f}</span>
          ))}
          {p.features.length > 3 && (
            <span className="alpha-chip alpha-chip-neutral !text-[11px] !py-1 !px-2.5 !bg-[color:var(--alpha-purple-50)] !text-[color:var(--alpha-purple-700)] !border-[color:var(--alpha-purple-200)]">
              +{p.features.length - 3} more
            </span>
          )}
        </div>
        <div className="mt-5 pt-4 flex items-center justify-between border-t border-[color:var(--alpha-line)]/70">
          <span className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--alpha-purple-700)] font-bold inline-flex items-center gap-1.5 alpha-link-u">
            View home detail
          </span>
          <span className="w-10 h-10 rounded-full bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center group-hover:bg-[color:var(--alpha-purple-600)] group-hover:text-white group-hover:translate-x-0.5 transition-all">
            <ChevronRight className="w-5 h-5" />
          </span>
        </div>
      </div>
    </article>
  );
}

export default function FeaturedHome({ testid = "featured-home" } = {}) {
  return (
    <section className="relative overflow-hidden text-white" data-testid={testid}>
      {/* Cinematic dark backdrop — restrained */}
      <div className="absolute inset-0 bg-[#0F0625]" aria-hidden />
      <div className="alpha-mesh alpha-mesh-dark" aria-hidden style={{ opacity: 0.7 }} />
      <div className="alpha-grain-dark" aria-hidden />
      <div className="absolute -left-32 top-10 w-[460px] h-[460px] rounded-full pointer-events-none alpha-glow-a"
        style={{ background: "radial-gradient(closest-side, rgba(134,89,199,0.45), transparent 70%)", filter: "blur(60px)" }} aria-hidden />
      <div className="absolute -right-32 bottom-10 w-[460px] h-[460px] rounded-full pointer-events-none alpha-glow-b"
        style={{ background: "radial-gradient(closest-side, rgba(79,165,110,0.35), transparent 70%)", filter: "blur(60px)" }} aria-hidden />

      <div className="relative alpha-container py-14 md:py-20">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-7 md:mb-10 alpha-curtain">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full alpha-glass-dark text-white text-[11px] uppercase tracking-[0.22em] font-semibold">
              <Sparkles className="w-3.5 h-3.5 text-[color:var(--alpha-green-500)]" /> Featured Alpha homes
            </div>
            <h2 className="mt-4 font-display font-medium tracking-tight leading-[1.02] !text-white alpha-press" style={{ fontSize: "clamp(30px, 4.4vw, 56px)" }}>
              Real homes. <span className="alpha-serif-italic" style={{
                background: "linear-gradient(92deg, #C8B4F0 0%, #B6E0C5 60%, #C8B4F0 100%)",
                WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
              }}>Browse like real estate</span>.
            </h2>
            <p className="mt-3 text-white/80 max-w-xl text-[15px]">
              Accessible SIL homes delivered with 1:1 and 1:2 support only — never higher ratios.
            </p>
          </div>
          <Link to="/vacancies" className="alpha-glass-dark border border-white/30 text-white rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 hover:bg-white/15 transition-colors">
            All homes <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-2 gap-5 md:gap-7">
          {PROPERTIES.map((p) => <PropertyCard key={p.slug} p={p} />)}
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-white/75">
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[color:var(--alpha-green-500)]" /> Registered NDIS provider</span>
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[color:var(--alpha-green-500)]" /> Practice Standards aligned</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[color:var(--alpha-green-500)]" /> 24/7 on-call</span>
          <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", "featured-homes-strip")} className="inline-flex items-center gap-1.5 font-semibold text-white hover:text-white/85">
            <Phone className="w-3.5 h-3.5" /> 1300 380 692
          </a>
        </div>
      </div>
    </section>
  );
}
